import { SaveOutlined, SelectOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  Grid,
  Row,
  Space,
  Spin,
  Tabs,
  Tooltip,
  message
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { errorHandler } from '../utils';
import { instanceRef } from '../utils/axios';
import { fieldTypes } from '../utils/filter-utils';
import MonedaSelector from './MonedaSelector';
import PlanListMultiple from './PlanListMultiple';
import ResponsiveContainer from './ResponsiveContainer';
import SelectableListModal from './SelectableListModal';

const { Panel } = Collapse;
const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

function PanelHeader(props) {
  const { empresa } = props;
  const urlImage = `/images/empresas/${empresa.codigo}.png`;
  return (
    <Row justify="start" align="middle">
      <Col md={3} xs={4}>
        <img
          src={urlImage}
          alt="imagen aseguradora"
          width="100%"
          style={{
            verticalAlign: 'top',
            maxHeight: '68px',
            objectFit: 'contain'
          }}
        />
      </Col>
      <Col md={21} xs={20}>
        <Row align="middle">
          <Col
            md={14}
            xs={24}
            style={{
              textAlign: 'left',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              paddingLeft: 20,
              paddingRight: 20
            }}
          >
            {empresa && empresa.nombre}
          </Col>
          <Col
            md={10}
            xs={24}
            style={{
              textAlign: 'right'
            }}
          />
        </Row>
      </Col>
    </Row>
  );
}

const groupBy = (list, keyGetter, dataGetter) => {
  const map = new Map();
  list.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, {
        data: dataGetter ? dataGetter(item) : item,
        list: [item]
      });
    } else {
      collection.list.push(item);
    }
  });
  return map;
};

const PlanesAsignacion = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState({});
  const [planesAseguradoraProducto, setPlanesAseguradoraProducto] = useState();
  const [selectedPlanes, setSelectedPlanes] = useState([]);
  const [planesAdesasignar, setPlanesAdesasignar] = useState([]);
  const [planesEspecificos, setPlanesEspecificos] = useState();
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState(false);
  const [monedas, setMonedas] = useState([]);
  const [comisionesPorDefecto, setComisionesPorDefecto] = useState([]);
  const [comisionesAseguradoraList, setComisionesAseguradoraList] = useState(
    []
  );
  const [monedaProductoList, setMonedaProductoList] = useState([]);
  const [planLandingList, setPlanLandingList] = useState([]); // [{aseguradoraEmpresaProductoId, idPlanLanding, idPlanLandingAux}]

  const history = useHistory();
  const screens = useBreakpoint();
  const {
    params: { id: aseguradoraId }
  } = useRouteMatch();

  const columns = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      width: '20%',
      filterType: fieldTypes.STRING,
      sortable: true
    },
    {
      title: 'Nombre',
      dataIndex: 'descripcion',
      filterType: fieldTypes.STRING,
      sortable: true
    },
    {
      title: 'Dirección',
      dataIndex: 'direccion',
      filterType: fieldTypes.STRING,
      sortable: true
    },
    {
      title: 'Teléfono',
      dataIndex: 'telefono',
      filterType: fieldTypes.STRING,
      sortable: true
    } // TODO: Agregar columna que indique que hay configurado
  ];

  const configurarPlanes = async () => {
    history.push(`/admin-aseguradora-planes/${aseguradoraId}`);
  };
  const onChangeActiveTab = (indexCorredora, index, tab) => {
    setActiveTab({
      ...activeTab,
      [indexCorredora]: { [index]: tab }
    });
  };

  const getAseguradoraEmpresaProductoWithPlanesByAseguradora = async (
    id,
    actualizarSucursal
  ) => {
    // Planes Especificos
    try {
      const responseList = await instanceRef.instance.get(
        `/aseguradoraempresaproducto/planes`,
        { params: { aseguradoraProductoId: id } }
      );
      if (responseList.data.data) {
        const groupedEmpresa = groupBy(
          responseList.data.data,
          (item) => item.aseguradoraEmpresa?.empresa?.id || 'empty',
          (item) => item.aseguradoraEmpresa?.empresa
        );
        const sucursalesPreseleccionadas = {};
        groupedEmpresa.forEach((item) => {
          sucursalesPreseleccionadas[item.data.id] = item.data.sucursales.find(
            (suc) => suc.matriz
          );
        });
        if (actualizarSucursal) {
          setSucursalSeleccionada(sucursalesPreseleccionadas);
        }

        const groupedCorredora = groupBy(
          groupedEmpresa,
          (item) => item.data?.corredora?.id || 'empty',
          (item) => item.data.corredora
        );

        setPlanesEspecificos(groupedCorredora);

        const aepList = responseList.data.data;
        const planLandingListFiltered = aepList
          .filter((aep) => aep.id_plan_landing || aep.id_plan_landing_aux)
          .map((aep) => ({
            aseguradoraEmpresaProductoId: aep.id,
            ...(aep.id_plan_landing && { idPlanLanding: aep.id_plan_landing }),
            ...(aep.id_plan_landing_aux && {
              idPlanLandingAux: aep.id_plan_landing_aux
            })
          }));

        // estado que maneja el valor planLanding especial por cada aseguradoraEmpresaProducto

        setPlanLandingList(planLandingListFiltered);
      }
      setActiveTab({});
      return responseList.data.data;
    } catch (error) {
      return error;
    }
  };

  const getPlanesEstandaresByAseguradora = async ({ id }) => {
    try {
      const responseList = await instanceRef.instance.get(
        `/plan/aseguradoraproducto`,
        { params: { aseguradoraProductoId: id } }
      );
      if (responseList.data.data) {
        setPlanesAseguradoraProducto(responseList.data.data);
      }
      return responseList.data.data;
    } catch (error) {
      return null;
    }
  };

  const handleClickUpdate = async () => {
    try {
      setLoading(true);
      const selectedPlanesEstandares = selectedPlanes.filter(
        (planProducto) =>
          planProducto.empresaId && planProducto.id_plan !== null
      );

      const selectedPlanesEspecificos = selectedPlanes.filter(
        (planProducto) =>
          planProducto.empresaId &&
          planProducto.id_aseguradora_empresa_producto !== null &&
          planProducto.id_aseguradora_producto !== null &&
          planProducto.id_plan === null
      );

      const reCaptchaToken = await executeRecaptcha('plan/asignacion');
      await instanceRef.instance.post(`/plan/asignacion`, {
        reCaptchaToken,
        planesEstandar: selectedPlanesEstandares,
        planesEspecifico: selectedPlanesEspecificos,
        planesAdesasignar,
        comisionesPorDefecto,
        planLandingPorDefectoList: planLandingList
      });

      // Guardar grupo de comisión por defecto para cliente

      // Refresh de datos
      await fetchData();
      await fetchComisionAseguradoraEmpresaProducto();

      message.success('Planes actualizados correctamente');
      setLoading(false);
      return null;
    } catch (error) {
      errorHandler(error);
      setLoading(false);
      return error;
    }
  };

  const ButtonConfigurar = () => (
    <Button type="link" onClick={configurarPlanes}>
      Ir a configuración de planes
    </Button>
  );

  const onChangeMoneda = (value, item) => {
    const selected = monedas.find((moneda) => moneda.codigo === value);

    monedaProductoList.map((monedaProducto, index) => {
      if (
        monedaProducto.idProducto === item.producto.id &&
        monedaProducto.idEmpresa === item.aseguradoraEmpresa.id_empresa
      ) {
        // Si el producto ya existe en el listado, entonces se elimina y adelante se vuelve agregar con la nueva moneda seleccionada
        monedaProductoList.splice(index, 1);
      }
      return monedaProducto;
    });
    // En el state se agregan los productos cuya moneda se haya seleccionado/modificado
    setMonedaProductoList([
      ...monedaProductoList,
      {
        idEmpresa: item.aseguradoraEmpresa.id_empresa,
        idProducto: item.producto.id,
        moneda: selected
      }
    ]);
  };

  function PanelHeaderComision(props) {
    const { comision, porDefectoMatriz } = props;
    return (
      <Row justify="start" align="middle">
        <Col
          style={{
            textAlign: 'start',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            width: '100%',
            paddingLeft: 20,
            paddingRight: 20
          }}
        >
          Rango Comisión - {comision.valor}%
          {porDefectoMatriz && (
            <span style={{ fontWeight: 'bold' }}>
              {' '}
              | Comisión por defecto de la Matriz{' '}
            </span>
          )}{' '}
        </Col>
      </Row>
    );
  }

  const getMonedaPorProducto = (item) => {
    const idProducto = item.producto.id;
    const idEmpresa = item.aseguradoraEmpresa.id_empresa;
    // Las monedas modificadas se encuentran guardadas por cada Empresa y Producto en el state monedaProductoList
    if (monedaProductoList.length > 0) {
      const productoEncontrado = monedaProductoList.filter(
        (mon) => mon.idProducto === idProducto && mon.idEmpresa === idEmpresa
      )[0];
      if (productoEncontrado) {
        return productoEncontrado.moneda;
      }
    }
    // Si la moneda no fue modificada, se maneja por defecto GS o Moneda Local
    const defaultMoneda = monedas.filter((moneda) => moneda.codigo === 'gs')[0];
    return defaultMoneda;
  };

  const getMonedas = async (planes) => {
    const monedasConfiguradas = {};
    planes.forEach((item) => {
      monedasConfiguradas[item.moneda.codigo] = item.moneda;
    });
    setMonedas(Object.values(monedasConfiguradas));
  };

  const getSelectedIndex = (planList) => {
    const planesAsignados = planList.filter((plan) => plan.activo);
    const indexs = [];
    planList.forEach((plan, index) => {
      if (planesAsignados) {
        planesAsignados.forEach((item) => {
          if (item.id === plan.id) {
            indexs.push(index);
          }
        });
      }
    });

    return indexs;
  };

  const obtenerPlanesPorTipoMoneda = (tipo, planList, model, moneda) => {
    if (!moneda) {
      return [];
    }
    if (tipo === 'estandar') {
      /* Los estandares son planes relacionados a la aseguradora,
        si la empresa tiene asignado un plan estandar (es una copia del estandar original) y su referencia esta en id_plan
      */
      const auxPlanesAsignados = planList.filter(
        (plan) => plan.id_plan && plan.activo
      );
      const planesEstandares = planList.filter((plan) => {
        // Estandares habilitados
        return (
          !plan.id_aseguradora_empresa_producto &&
          !auxPlanesAsignados.find((item) => item.id_plan === plan.id)
        );
      });

      return [...auxPlanesAsignados, ...planesEstandares]
        .filter((item) => item.moneda.codigo === moneda.codigo)
        .sort((a, b) => a.orden - b.orden);
    }
    if (tipo === 'especifico') {
      const idEmpresa = model.aseguradoraEmpresa.id_empresa;
      const sucursal = sucursalSeleccionada[idEmpresa];
      const auxPlanesEspecificos = planList.filter(
        (plan) =>
          !plan.id_aseguradora_producto &&
          plan.id_sucursal &&
          plan.id_sucursal === sucursal.id
      );
      const planesAsignadosEsp = planList.filter(
        (plan) =>
          plan.activo &&
          !plan.id_plan &&
          plan.id_sucursal &&
          plan.id_sucursal === sucursal.id
      );

      return [...auxPlanesEspecificos, ...planesAsignadosEsp]
        .filter((item) => item.moneda.codigo === moneda.codigo)
        .sort((a, b) => a.orden - b.orden);
    }

    return null;
  };

  const getPlanesEstandarPorProducto = (producto) =>
    planesAseguradoraProducto?.filter(
      (mon) => mon.aseguradoraProducto.producto.id === producto.id
    ) || [];

  const updateSucursalSeleccionada = (empresa, sucursal) => {
    const sucursalSeleccionadaNuevo = { ...sucursalSeleccionada };
    sucursalSeleccionadaNuevo[empresa.id] = sucursal;
    setSucursalSeleccionada(sucursalSeleccionadaNuevo);
  };

  const esEstandarAsignado = (item, plan, aseguradoraEmpresaProductoId) =>
    item.id === plan.id &&
    item.id_aseguradora_empresa_producto === aseguradoraEmpresaProductoId;

  const onChangePlanLanding = (newIndex, aep, planList, esEstandar, isAux) => {
    const aseguradoraEmpresaProducto = aep;
    // Funcion que modifica el valor del objeto principal de aseguradoraEmpresaProducto y el estado planLandingList con el id del plan quee fue seleccionado "landing" o "landing auxiliar"
    // Para estandares y Espcificos, se puede seleccionar un solo valor entre ambos
    let planLandingSelected;
    if (esEstandar) {
      planLandingSelected = selectedPlanes.find((item) =>
        esEstandarAsignado(
          item,
          planList[newIndex],
          aseguradoraEmpresaProducto.id
        )
      );
    } else {
      planLandingSelected = selectedPlanes.find(
        (item) => item.id === planList[newIndex].id
      );
    }

    if (planLandingSelected) {
      if (!isAux) {
        aseguradoraEmpresaProducto.id_plan_landing = planLandingSelected.id;
      } else {
        aseguradoraEmpresaProducto.id_plan_landing_aux = planLandingSelected.id;
      }

      setPlanLandingList((prevList) => {
        // Verifica si aseguradoraEmpresaProducto ya existe en la lista
        const index = prevList.findIndex(
          (item) =>
            item.aseguradoraEmpresaProductoId === aseguradoraEmpresaProducto.id
        );

        // Si ya existe, actualiza el idPlanLanding o idPlanLandingAux
        if (index !== -1) {
          const updatedList = prevList.map((item, i) => {
            if (i === index) {
              // Crea una copia del objeto para modificarlo
              const updatedItem = { ...item };
              if (!isAux) {
                updatedItem.idPlanLanding = planLandingSelected.id;
              } else {
                updatedItem.idPlanLandingAux = planLandingSelected.id;
              }
              return updatedItem;
            }
            return item;
          });
          return updatedList;
        }

        // Si no existe, agrega el nuevo objeto a la lista
        const newItem = {
          aseguradoraEmpresaProductoId: aseguradoraEmpresaProducto.id,
          ...(!isAux && { idPlanLanding: planLandingSelected.id }),
          ...(isAux && { idPlanLandingAux: planLandingSelected.id })
        };

        return [...prevList, newItem];
      });
    } else {
      // TODO: si el plan no esta asignado no puede ser seleccionado para landing, se debe realizar un unchecked
    }
  };

  const onChangePlanSelectorEstandar = (
    value,
    selectedIndexs,
    planList,
    aseguradoraProductoId,
    aseguradoraEmpresaProductoId,
    empresaId
  ) => {
    const existePlan = selectedIndexs.find((item) => item === value);
    const existeEstandarAsignadoActivo = selectedPlanes.find((item) =>
      esEstandarAsignado(item, planList[value], aseguradoraEmpresaProductoId)
    );
    // Si es estandar
    if ((existePlan || existePlan === 0) && existeEstandarAsignadoActivo) {
      // Se deselecciona el plan
      setSelectedPlanes(
        selectedPlanes.filter(
          (item) =>
            !esEstandarAsignado(
              item,
              planList[value],
              aseguradoraEmpresaProductoId
            )
        )
      );
      if (planList[value].activo && setPlanesAdesasignar) {
        setPlanesAdesasignar([...planesAdesasignar, planList[value]]);
      }

      // Si el plan se encontraba activo, enviar entre la lista de desasignados
    } else {
      // Se selecciona el plan
      let planToAdd = planList[value];
      // si es un plan estandar seleccionado, se asigna el id_aseguradora_empresa_producto, id_plan (padre)
      // si es un plan especifico seleccionado, se asigna el id_aseguradora_producto

      if (planToAdd.activo && setPlanesAdesasignar) {
        setPlanesAdesasignar(
          planesAdesasignar.filter((item) => item !== planList[value])
        );
      }

      planToAdd = {
        ...planToAdd,
        id_plan: !planToAdd.id_aseguradora_empresa_producto
          ? planToAdd.id
          : null,
        id_aseguradora_empresa_producto: planToAdd.id_aseguradora_empresa_producto
          ? planToAdd.id_aseguradora_empresa_producto
          : aseguradoraEmpresaProductoId,
        id_aseguradora_producto:
          planToAdd.id_aseguradora_empresa_producto &&
          !planToAdd.id_aseguradora_producto
            ? aseguradoraProductoId
            : planToAdd.id_aseguradora_producto,
        empresaId
      };

      setSelectedPlanes([...selectedPlanes, planToAdd]);
    }
  };

  const onChangePlanSelectorEspecifico = (
    value,
    selectedIndexs,
    planList,
    aseguradoraProductoId,
    aseguradoraEmpresaProductoId,
    empresaId
  ) => {
    const existePlan = selectedIndexs.find((item) => item === value);

    if (existePlan || existePlan === 0) {
      // Se deselecciona el plan
      setSelectedPlanes(
        selectedPlanes.filter((item) => item.id !== planList[value].id)
      );

      if (planList[value].activo && setPlanesAdesasignar) {
        setPlanesAdesasignar([...planesAdesasignar, planList[value]]);
      }

      // Si el plan se encontraba activo, enviar entre la lista de desasignados
    } else {
      // Se selecciona el plan
      let planToAdd = planList[value];
      // si es un plan estandar seleccionado, se asigna el id_aseguradora_empresa_producto, id_plan (padre)
      // si es un plan especifico seleccionado, se asigna el id_aseguradora_producto

      if (planToAdd.activo && setPlanesAdesasignar) {
        setPlanesAdesasignar(
          planesAdesasignar.filter((item) => item !== planList[value])
        );
      }

      planToAdd = {
        ...planToAdd,
        id_plan: !planToAdd.id_aseguradora_empresa_producto
          ? planToAdd.id
          : null,
        id_aseguradora_empresa_producto: planToAdd.id_aseguradora_empresa_producto
          ? planToAdd.id_aseguradora_empresa_producto
          : aseguradoraEmpresaProductoId,
        id_aseguradora_producto:
          planToAdd.id_aseguradora_empresa_producto &&
          !planToAdd.id_aseguradora_producto
            ? aseguradoraProductoId
            : planToAdd.id_aseguradora_producto,
        empresaId
      };
      setSelectedPlanes([...selectedPlanes, planToAdd]);
    }
  };

  const SucursalSelector = ({ empresa }) => {
    return (
      <div
        style={{
          marginRight: 16
        }}
      >
        <SelectableListModal
          textButton="Sucursales"
          title="Listado de sucursales"
          placeholder="Buscar sucursales"
          width={1000}
          resetValue
          notNull
          button={
            <Button icon={<SelectOutlined />} type="primary" ghost>
              {sucursalSeleccionada?.[empresa.id]?.descripcion || ''}
            </Button>
          }
          columnsData={columns}
          url={`/sucursal?id_empresa=${empresa.id}`}
          setNewList={(newValue) => {
            updateSucursalSeleccionada(empresa, newValue[0]);
          }}
        />
      </div>
    );
  };

  const fetchData = async (actualizarSucursal) => {
    setLoading(true);
    // Para planes Especificos, se obtiene todos los planes desde la AseguradoraEmpresaProducto
    const planesEsp = await getAseguradoraEmpresaProductoWithPlanesByAseguradora(
      aseguradoraId,
      actualizarSucursal
    );

    // Para planes Estandares se obtienen todos los planes para la aseguradora producto que no sean especificos
    let planesEst = await getPlanesEstandaresByAseguradora({
      id: aseguradoraId
    });

    planesEst = planesEst || [];
    const todos = [
      ...planesEsp.flatMap((esp) => esp.planes),
      ...planesEst.flatMap((esp) => esp.planes)
    ];

    const indexs = getSelectedIndex(todos.filter((item) => item));
    setSelectedPlanes(todos.filter((_, index) => indexs.includes(index)));
    getMonedas([...planesEsp.flatMap((esp) => esp.planes), ...planesEst]);
    setLoading(false);
    setPlanesAdesasignar([]);
  };

  const onChangeComisionCliente = (
    idAseguradoraEmpresaProducto,
    idComisionAseguradora,
    tipo
  ) => {
    const key = `${idAseguradoraEmpresaProducto}-${idComisionAseguradora}-${tipo}`;
    const encontrado = comisionesPorDefecto.find((item) => item.key === key);
    let nuevaLista;
    if (encontrado) {
      nuevaLista = comisionesPorDefecto.filter((item) => item.key !== key);

      if (encontrado.agregado) {
        setComisionesPorDefecto(nuevaLista);
        return;
      }

      if (encontrado.eliminado) {
        delete encontrado.eliminado;
        if (encontrado.agregado) {
          setComisionesPorDefecto(nuevaLista);
          return;
        }
      } else {
        encontrado.eliminado = true;
      }

      nuevaLista.push(encontrado);
    } else {
      nuevaLista = [
        ...comisionesPorDefecto,
        {
          key,
          id_aseguradora_empresa_producto: idAseguradoraEmpresaProducto,
          id_comision_aseguradora: idComisionAseguradora,
          tipo,
          agregado: true
        }
      ];
    }
    setComisionesPorDefecto(nuevaLista);
  };

  const getAseguradoraEmpresaProducto = (planes) => {
    const item = planes.find(
      (elemento) => elemento.id_aseguradora_empresa_producto
    );
    return item?.id_aseguradora_empresa_producto;
  };

  const yaSeleccionado = (planes, comision, tipo) => {
    return comisionesPorDefecto.some(
      (i) =>
        i.tipo === tipo &&
        i.id_aseguradora_empresa_producto ===
          getAseguradoraEmpresaProducto(planes) &&
        i.id_comision_aseguradora !== comision.id &&
        !i.eliminado
    );
  };

  const checkedOption = (planes, tipo) => {
    return comisionesPorDefecto
      ?.filter((i) => !i.eliminado)
      ?.find(
        (i) =>
          i.key ===
          `${getAseguradoraEmpresaProducto(planes)}-${
            planes[0]?.id_comision_aseguradora
          }-${tipo}`
      );
  };

  const fetchComisionesAseguradora = async (idAseguradora) => {
    try {
      const response = await instanceRef.instance.get(`/comisionaseguradora`, {
        params: { idAseguradora }
      });
      if (response.data?.data) {
        setComisionesAseguradoraList(response.data?.data);
      }
    } catch (error) {
      errorHandler(
        error,
        'No se pudo obtener las comisiones de la Aseguradora'
      );
      setLoading(false);
    }
  };

  const fetchComisionAseguradoraEmpresaProducto = async () => {
    try {
      const response = await instanceRef.instance.get(
        `/comisionaseguradoraempresaproducto/all`
      );
      if (response.data?.data) {
        const comisiones = response.data?.data.map((obj) => {
          return {
            ...obj,
            key: `${obj.id_aseguradora_empresa_producto}-${obj.id_comision_aseguradora}-${obj.tipo}`
          };
        });
        setComisionesPorDefecto(comisiones);
      }
    } catch (error) {
      errorHandler(
        error,
        'No se pudo obtener las comisiones de la Aseguradora Empresa Producto'
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    // Refresh de datos
    fetchData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchComisionesAseguradora(aseguradoraId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Settear comisiones por defecto
    fetchComisionAseguradoraEmpresaProducto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ResponsiveContainer size="large">
      <Card title="Asignación de planes" style={{ textAlign: 'left' }}>
        <>
          <Spin spinning={loading} delay={500} size="large">
            {planesEspecificos &&
              Array.from(planesEspecificos.values()).map(
                (corredora, indexCorredora) => (
                  <Card
                    key={indexCorredora}
                    size="small"
                    style={{ marginBottom: 20 }}
                    title={corredora.data?.nombre || 'Sin Corredora'}
                  >
                    <Collapse expandIconPosition="end" size="small">
                      {planesEspecificos &&
                        Array.from(corredora.list.values()).map(
                          (value, index) => {
                            return (
                              <Panel
                                header={<PanelHeader empresa={value.data} />}
                                key={index}
                                className="collapse-centered"
                              >
                                <div className="site-card-wrapper">
                                  <Row gutter={24}>
                                    <Col span={24}>
                                      <Tabs
                                        activeKey={
                                          activeTab[indexCorredora]?.[index]
                                        }
                                        size="large"
                                        onChange={(tab) =>
                                          onChangeActiveTab(
                                            indexCorredora,
                                            index,
                                            tab
                                          )
                                        }
                                      >
                                        {value.list.map(
                                          (item, indexProductos) => {
                                            const monedaPorProducto = getMonedaPorProducto(
                                              item
                                            );
                                            const aseguradoraProductoId =
                                              item.producto
                                                .aseguradoraProducto[0].id;
                                            const empresaId =
                                              item.aseguradoraEmpresa
                                                .id_empresa;

                                            const planesEstPorMonedaSuc = obtenerPlanesPorTipoMoneda(
                                              'estandar',
                                              [
                                                ...getPlanesEstandarPorProducto(
                                                  item.producto
                                                ),
                                                ...item.planes
                                              ],
                                              item,
                                              monedaPorProducto
                                            );

                                            const planesEspPorMonedaSuc = obtenerPlanesPorTipoMoneda(
                                              'especifico',
                                              item.planes,
                                              item,
                                              monedaPorProducto
                                            );

                                            return (
                                              <TabPane
                                                tab={item.producto.descripcion}
                                                key={indexProductos}
                                              >
                                                {comisionesAseguradoraList &&
                                                  comisionesAseguradoraList.map(
                                                    (comision) => {
                                                      // Específicos
                                                      const selectedIndexsEsp = [];
                                                      let selectedIndexLandingEsp;
                                                      let selectedIndexLandingAuxEsp;
                                                      const planesEspPorMonedaSucCom = planesEspPorMonedaSuc.filter(
                                                        (i) =>
                                                          i.id_comision_aseguradora ===
                                                          comision.id
                                                      );
                                                      for (
                                                        let i = 0;
                                                        i <
                                                        planesEspPorMonedaSucCom.length;
                                                        i += 1
                                                      ) {
                                                        const element =
                                                          planesEspPorMonedaSucCom[
                                                            i
                                                          ];
                                                        if (
                                                          selectedPlanes.find(
                                                            (el2) =>
                                                              el2.id ===
                                                              element.id
                                                          )
                                                        ) {
                                                          selectedIndexsEsp.push(
                                                            i
                                                          );
                                                        }

                                                        if (
                                                          planLandingList.find(
                                                            (planLanding) =>
                                                              planLanding.aseguradoraEmpresaProductoId ===
                                                                item.id &&
                                                              planLanding.idPlanLanding ===
                                                                element.id
                                                          )
                                                        ) {
                                                          selectedIndexLandingEsp = i;
                                                        }

                                                        if (
                                                          planLandingList.find(
                                                            (planLanding) =>
                                                              planLanding.aseguradoraEmpresaProductoId ===
                                                                item.id &&
                                                              planLanding.idPlanLandingAux ===
                                                                element.id
                                                          )
                                                        ) {
                                                          selectedIndexLandingAuxEsp = i;
                                                        }
                                                      }
                                                      // Estandares
                                                      const selectedIndexsEst = [];
                                                      let selectedIndexLandingEst;
                                                      let selectedIndexLandingAuxEst;

                                                      const planesEstPorMonedaSucCom = planesEstPorMonedaSuc.filter(
                                                        (i) =>
                                                          i.id_comision_aseguradora ===
                                                          comision.id
                                                      );
                                                      for (
                                                        let i = 0;
                                                        i <
                                                        planesEstPorMonedaSucCom.length;
                                                        i += 1
                                                      ) {
                                                        const element =
                                                          planesEstPorMonedaSucCom[
                                                            i
                                                          ];
                                                        if (
                                                          selectedPlanes.find(
                                                            (el2) =>
                                                              el2.id ===
                                                                element.id &&
                                                              el2.id_aseguradora_empresa_producto ===
                                                                item.id
                                                          )
                                                        ) {
                                                          selectedIndexsEst.push(
                                                            i
                                                          );
                                                        }
                                                        if (
                                                          selectedPlanes.find(
                                                            (planSelected) =>
                                                              item.id_plan_landing &&
                                                              planSelected.id ===
                                                                item.id_plan_landing &&
                                                              planSelected.id_aseguradora_empresa_producto ===
                                                                item.id &&
                                                              planSelected.id ===
                                                                element.id
                                                          )
                                                        ) {
                                                          selectedIndexLandingEst = i;
                                                        }
                                                        if (
                                                          selectedPlanes.find(
                                                            (planSelected) =>
                                                              item.id_plan_landing_aux &&
                                                              planSelected.id ===
                                                                item.id_plan_landing_aux &&
                                                              planSelected.id_aseguradora_empresa_producto ===
                                                                item.id &&
                                                              planSelected.id ===
                                                                element.id
                                                          )
                                                        ) {
                                                          selectedIndexLandingAuxEst = i;
                                                        }
                                                      }
                                                      const comisionPorDefectoMatriz = comisionesPorDefecto.find(
                                                        (e) =>
                                                          e.id_comision_aseguradora ===
                                                            comision.id &&
                                                          e.id_aseguradora_empresa_producto ===
                                                            item.id
                                                      );
                                                      return (
                                                        (planesEstPorMonedaSucCom.length >
                                                          0 ||
                                                          planesEspPorMonedaSucCom.length >
                                                            0) && (
                                                          <Collapse expandIconPosition="end">
                                                            <Panel
                                                              header={
                                                                <PanelHeaderComision
                                                                  comision={
                                                                    comision
                                                                  }
                                                                  porDefectoMatriz={
                                                                    !!comisionPorDefectoMatriz
                                                                  }
                                                                />
                                                              }
                                                              key={`planes${comision.id}`}
                                                              style={{
                                                                marginBottom:
                                                                  '2%'
                                                              }}
                                                            >
                                                              <>
                                                                {' '}
                                                                {monedas &&
                                                                  monedas.length >
                                                                    0 && (
                                                                    <MonedaSelector
                                                                      value={
                                                                        monedaPorProducto
                                                                          ? monedaPorProducto.codigo
                                                                          : null
                                                                      }
                                                                      monedas={
                                                                        monedas
                                                                      }
                                                                      onChange={(
                                                                        e
                                                                      ) => {
                                                                        onChangeMoneda(
                                                                          e
                                                                            .target
                                                                            .value,
                                                                          item
                                                                        );
                                                                      }}
                                                                    />
                                                                  )}
                                                                <br />
                                                                <br />
                                                                {(item.planes ||
                                                                  planesAseguradoraProducto) &&
                                                                monedas.length >
                                                                  0 ? (
                                                                  <div
                                                                    style={{
                                                                      display:
                                                                        screens &&
                                                                        screens.md ===
                                                                          false
                                                                          ? ''
                                                                          : 'inline-block'
                                                                    }}
                                                                  >
                                                                    <Space
                                                                      direction="vertical"
                                                                      align="start"
                                                                    >
                                                                      <div
                                                                        style={{
                                                                          display:
                                                                            'flex'
                                                                        }}
                                                                      >
                                                                        <Tooltip title="Si está seleccionado, será el grupo por defecto para el cliente (autogestión).">
                                                                          {planesEstPorMonedaSucCom.length >
                                                                            0 && (
                                                                            <Checkbox
                                                                              style={{
                                                                                background:
                                                                                  '#dcdfe3',
                                                                                borderRadius:
                                                                                  '6px',
                                                                                margin: 0
                                                                              }}
                                                                              checked={checkedOption(
                                                                                planesEstPorMonedaSucCom,
                                                                                'estandar'
                                                                              )}
                                                                              disabled={
                                                                                !getAseguradoraEmpresaProducto(
                                                                                  planesEstPorMonedaSucCom
                                                                                ) ||
                                                                                yaSeleccionado(
                                                                                  planesEstPorMonedaSucCom,
                                                                                  comision,
                                                                                  'estandar'
                                                                                )
                                                                              }
                                                                              onChange={() => {
                                                                                onChangeComisionCliente(
                                                                                  getAseguradoraEmpresaProducto(
                                                                                    planesEstPorMonedaSucCom
                                                                                  ),
                                                                                  planesEstPorMonedaSucCom[0]
                                                                                    .id_comision_aseguradora,
                                                                                  'estandar'
                                                                                );
                                                                              }}
                                                                            />
                                                                          )}
                                                                        </Tooltip>
                                                                        <PlanListMultiple
                                                                          key={`estandar-${item.producto.codigo}`}
                                                                          tituloAdicional="Detalle de Cobertura Estándar"
                                                                          planes={
                                                                            planesEstPorMonedaSucCom
                                                                          }
                                                                          selectedIndexs={
                                                                            selectedIndexsEst
                                                                          }
                                                                          onChange={(
                                                                            newValue,
                                                                            isLandingPorDefecto,
                                                                            isAux
                                                                          ) => {
                                                                            if (
                                                                              !isLandingPorDefecto
                                                                            ) {
                                                                              onChangePlanSelectorEstandar(
                                                                                newValue,
                                                                                selectedIndexsEst,
                                                                                planesEstPorMonedaSucCom,
                                                                                aseguradoraProductoId,
                                                                                item.id,
                                                                                empresaId
                                                                              );
                                                                            } else {
                                                                              onChangePlanLanding(
                                                                                newValue,
                                                                                item,
                                                                                planesEstPorMonedaSucCom,
                                                                                true,
                                                                                isAux
                                                                              );
                                                                            }
                                                                          }}
                                                                          moneda={
                                                                            monedaPorProducto
                                                                          }
                                                                          mostrarLandingPorDefecto
                                                                          landingPorDefectoIndex={
                                                                            selectedIndexLandingEst
                                                                          }
                                                                          landingPorDefectoAuxIndex={
                                                                            selectedIndexLandingAuxEst
                                                                          }
                                                                        />{' '}
                                                                      </div>
                                                                      <br />
                                                                      <SucursalSelector
                                                                        empresa={
                                                                          value.data
                                                                        }
                                                                      />
                                                                      <br />
                                                                      <div
                                                                        style={{
                                                                          display:
                                                                            'flex'
                                                                        }}
                                                                      >
                                                                        <Tooltip title="Si está seleccionado, será el grupo por defecto para el cliente (autogestión).">
                                                                          {planesEspPorMonedaSucCom.length >
                                                                            0 && (
                                                                            <Checkbox
                                                                              disabled={
                                                                                !getAseguradoraEmpresaProducto(
                                                                                  planesEspPorMonedaSucCom
                                                                                ) ||
                                                                                yaSeleccionado(
                                                                                  planesEspPorMonedaSucCom,
                                                                                  comision,
                                                                                  'especifico'
                                                                                )
                                                                              }
                                                                              checked={checkedOption(
                                                                                planesEspPorMonedaSucCom,
                                                                                'especifico'
                                                                              )}
                                                                              onChange={() => {
                                                                                onChangeComisionCliente(
                                                                                  getAseguradoraEmpresaProducto(
                                                                                    planesEspPorMonedaSucCom
                                                                                  ),
                                                                                  planesEspPorMonedaSucCom[0]
                                                                                    .id_comision_aseguradora,
                                                                                  'especifico'
                                                                                );
                                                                              }}
                                                                              style={{
                                                                                background:
                                                                                  '#dcdfe3',
                                                                                borderRadius:
                                                                                  '6px',
                                                                                margin: 0
                                                                              }}
                                                                            />
                                                                          )}
                                                                        </Tooltip>
                                                                        <PlanListMultiple
                                                                          key={`especifico-${item.producto.codigo}`}
                                                                          tituloAdicional="Detalle de Cobertura Específico"
                                                                          planes={
                                                                            planesEspPorMonedaSucCom
                                                                          }
                                                                          selectedIndexs={
                                                                            selectedIndexsEsp
                                                                          }
                                                                          onChange={(
                                                                            newValue,
                                                                            isLandingPorDefecto,
                                                                            isAux
                                                                          ) => {
                                                                            if (
                                                                              !isLandingPorDefecto
                                                                            ) {
                                                                              onChangePlanSelectorEspecifico(
                                                                                newValue,
                                                                                selectedIndexsEsp,
                                                                                planesEspPorMonedaSucCom,
                                                                                aseguradoraProductoId,
                                                                                item.id,
                                                                                empresaId
                                                                              );
                                                                            } else {
                                                                              onChangePlanLanding(
                                                                                newValue,
                                                                                item,
                                                                                planesEspPorMonedaSucCom,
                                                                                false,
                                                                                isAux
                                                                              );
                                                                            }
                                                                          }}
                                                                          moneda={
                                                                            monedaPorProducto
                                                                          }
                                                                          mostrarLandingPorDefecto
                                                                          landingPorDefectoIndex={
                                                                            selectedIndexLandingEsp
                                                                          }
                                                                          landingPorDefectoAuxIndex={
                                                                            selectedIndexLandingAuxEsp
                                                                          }
                                                                        />
                                                                      </div>
                                                                    </Space>
                                                                  </div>
                                                                ) : (
                                                                  <ButtonConfigurar />
                                                                )}
                                                              </>
                                                            </Panel>
                                                          </Collapse>
                                                        )
                                                      );
                                                    }
                                                  )}
                                              </TabPane>
                                            );
                                          }
                                        )}
                                      </Tabs>
                                    </Col>
                                  </Row>
                                </div>
                              </Panel>
                            );
                          }
                        )}
                    </Collapse>
                  </Card>
                )
              )}
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleClickUpdate}
              >
                Actualizar
              </Button>
            </Space>
          </Spin>
        </>
      </Card>
    </ResponsiveContainer>
  );
};

export default PlanesAsignacion;
